import React, { useState, useMemo, memo, useEffect } from 'react';
import { Box, Typography, InputBase, Button } from '@material-ui/core';
import { Search as SearchIcon, SwapVert as SwapVertIcon } from '@material-ui/icons';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { useSupportContext } from '../SupportContext';
import { CategoriesList } from './CategoriesList';
import { GridViewLanding } from './GridViewLanding';
import { AddNewButton } from './AddNewButton';
import { EnterpriseLockNotice, lockedTooltip } from '../../../../global/auth/use-enterprise-lock/use-enterprise-lock';
import { useCampfireLazyQuery } from '../../../../global/network/useCampfireLazyQuery';
import { GET_RESOURCES_GRID_VIEW } from '../../../admin/admin-console/admin-console-content-pages/admin-console-resources/admin-console-resources.gql';

const CatalogueLandingPage = memo(() => {
  const { allCategories, isAdmin, isEnterpriseLocked } = useSupportContext();
  const { isMobile, isSm } = useCampfireTheme();

  const [searchInput, setSearchInput] = useState<string>();
  const [gridViewEnabled, setGridViewEnabled] = useState(false);
  const [reorderMode, setReorderMode] = useState(false);

  const [queryGridView] = useCampfireLazyQuery(GET_RESOURCES_GRID_VIEW);

  useEffect(() => {
    queryGridView({}).then((response) => {
      const flagValue = (response.data as any)?.vm?.configItem?.configItemValue?.flagValue;
      setGridViewEnabled(flagValue ?? false);
    });
  }, []);

  const categories = useMemo(
    () =>
      searchInput
        ? allCategories
            .filter(
              (c) =>
                c.title.toLowerCase().includes(searchInput.toLowerCase()) ||
                c.supportResources.find((r) => r.title.toLowerCase().includes(searchInput.toLowerCase()))
            )
            .map((c) => ({
              ...c,
              supportResources: c.supportResources.filter((r) =>
                searchInput ? r.title.toLowerCase().includes(searchInput.toLowerCase()) : true
              ),
            }))
        : allCategories,
    [searchInput, allCategories]
  );

  return (
    <Box
      display='flex'
      flexDirection='column'
      flex={1}
      height='100%'
      overflow='auto'
      style={{ backgroundColor: '#f8f9fa' }}
    >
      {/* White header area: toolbar + search */}
      <Box style={{ backgroundColor: '#fff', flexShrink: 0, paddingBottom: 20 }}>
        {/* Header toolbar */}
        <Box
          style={{
            paddingLeft: isMobile || isSm ? 10 : 25,
            paddingRight: 10,
            paddingTop: 10,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography style={{ fontSize: 14, fontWeight: 700, color: '#333' }}>{'Resources'}</Typography>
          <Box display='flex' alignItems='center' style={{ gap: 8 }}>
            {isAdmin ? (
              <Button
                onClick={() => setReorderMode(!reorderMode)}
                style={{
                  backgroundColor: reorderMode ? '#7047EB' : 'transparent',
                  color: reorderMode ? '#fff' : '#646464',
                  border: '1px solid #d9d9d9',
                  borderRadius: 4,
                  padding: '4.5px 12px',
                  fontSize: 12,
                  fontWeight: 600,
                  textTransform: 'none',
                  minWidth: 'auto',
                }}
              >
                <SwapVertIcon style={{ fontSize: 16, marginRight: 4 }} />
                {reorderMode ? 'Done' : 'Reorder'}
              </Button>
            ) : null}
            {isAdmin ? <AddNewButton /> : null}
            {isEnterpriseLocked ? <EnterpriseLockNotice tooltip={lockedTooltip('Resources')} /> : null}
          </Box>
        </Box>

        {/* Search bar */}
        <Box
          style={{
            width: isMobile || isSm ? '95%' : '50%',
            marginTop: 20,
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          <Box style={{ display: 'flex', justifyContent: 'center' }}>
            <InputBase
              value={searchInput || ''}
              placeholder='Search resources by name or keyword'
              onChange={(e) => setSearchInput(e.target.value || undefined)}
              style={{
                border: '.5px solid #d9d9d9',
                borderRadius: '25px 0 0 25px',
                width: '85%',
                display: 'flex',
                padding: '6px 24px',
              }}
            />
            <Button
              style={{
                backgroundColor: '#7047EB',
                borderRadius: '0 25px 25px 0',
                width: '12%',
                display: 'flex',
                padding: '8px 10px',
              }}
              onClick={() => setSearchInput(undefined)}
            >
              <SearchIcon style={{ color: '#fff' }} />
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Content */}
      <Box
        style={{
          paddingLeft: isMobile || isSm ? 12 : 60,
          paddingRight: isMobile || isSm ? 12 : 60,
          paddingTop: 30,
          paddingBottom: 40,
        }}
      >
        {gridViewEnabled ? (
          <GridViewLanding categories={categories} />
        ) : (
          <CategoriesList categories={categories} reorderMode={reorderMode} />
        )}
      </Box>
    </Box>
  );
});

export { CatalogueLandingPage };
